import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, MapPin, Save, PenLine } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, STATUS_LABELS, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import JobForm from "@/components/JobForm";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");
const toDateInput = (iso) => (iso ? iso.slice(0, 10) : "");

export function JobDialog({ open, onOpenChange, job, staff, onSaved, initialDate, initialUser, onManual }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {job?.id
              ? t("jobForm.dialogOrder", { number: job.order_number ? String(job.order_number).padStart(4, "0") : "" })
              : t("jobForm.dialogNew")}
          </DialogTitle>
        </DialogHeader>
        {!job?.id && onManual && (
          <button
            data-testid="job-dialog-manual-link"
            onClick={() => { onOpenChange(false); onManual(); }}
            className="-mt-1 flex items-center gap-1.5 self-start rounded-md border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:bg-slate-100"
          >
            <PenLine className="h-3.5 w-3.5 text-slate-400" /> Manuell order – skriv in jobb i efterhand
          </button>
        )}
        {open && (
          <JobForm job={job} initialDate={initialDate} initialUser={initialUser} staff={staff} onSaved={onSaved} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function LegacyJobDialog({ open, onOpenChange, job, staff, onSaved }) {
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: job?.title || "",
      customer_name: job?.customer_name || "",
      address: job?.address || "",
      description: job?.description || "",
      start: toLocalInput(job?.start) || `${new Date().toISOString().slice(0, 11)}08:00`,
      end: toLocalInput(job?.end),
      status: job?.status || "planned",
      assigned_to: job?.assigned_to?.[0] || "none",
    });
  }, [open, job]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = leader
        ? {
            title: form.title,
            customer_name: form.customer_name,
            address: form.address,
            description: form.description,
            start: `${form.start.slice(0, 16)}:00`,
            end: form.end ? `${form.end.slice(0, 16)}:00` : null,
            status: form.status,
            assigned_to: form.assigned_to && form.assigned_to !== "none" ? [form.assigned_to] : [],
          }
        : { status: form.status, description: form.description };
      if (job?.id) await api.put(`/jobs/${job.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(job?.id ? "Jobb uppdaterat" : "Jobb skapat");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Ta bort jobbet permanent?")) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Jobb borttaget");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-dialog" className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {job?.id ? "Redigera jobb" : "Nytt jobb"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <L>Rubrik</L>
            <Input
              data-testid="job-title-input"
              disabled={!leader}
              value={form.title || ""}
              onChange={(e) => set("title", e.target.value)}
              className="mt-1 h-10"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <L>Kund</L>
              <Input
                data-testid="job-customer-input"
                disabled={!leader}
                value={form.customer_name || ""}
                onChange={(e) => set("customer_name", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>Status</L>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="job-status-select" className="mt-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`job-status-option-${k}`}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <L>Kundadress</L>
            <Input
              data-testid="job-address-input"
              disabled={!leader}
              value={form.address || ""}
              onChange={(e) => set("address", e.target.value)}
              className="mt-1 h-10"
            />
            {form.address && (
              <a
                data-testid="job-dialog-maps-link"
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(form.address)}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 transition-colors duration-200 hover:text-blue-700"
              >
                <MapPin className="h-4 w-4" /> Vägbeskrivning
              </a>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <L>Start</L>
              <Input
                type="datetime-local"
                data-testid="job-start-input"
                disabled={!leader}
                value={form.start || ""}
                onChange={(e) => set("start", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>Slut</L>
              <Input
                type="datetime-local"
                data-testid="job-end-input"
                disabled={!leader}
                value={form.end || ""}
                onChange={(e) => set("end", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>
          {leader && (
            <div>
              <L>Tilldelad</L>
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger data-testid="job-assignee-select" className="mt-1 h-10">
                  <SelectValue placeholder="Välj person" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Ingen</SelectItem>
                  {staff?.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`job-assignee-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <L>Beskrivning</L>
            <Textarea
              data-testid="job-description-input"
              value={form.description || ""}
              onChange={(e) => set("description", e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {job?.id && leader ? (
            <Button
              variant="outline"
              data-testid="job-delete-button"
              onClick={remove}
              className="border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Ta bort
            </Button>
          ) : (
            <span />
          )}
          <Button
            data-testid="job-save-button"
            onClick={save}
            disabled={busy}
            className="bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Spara</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AbsenceDialog({ open, onOpenChange, absence, staff, onSaved }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const leader = isLeader(user?.role);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      user_id: absence?.user_id || user?.id || "",
      type: absence?.type || "vacation",
      start: toDateInput(absence?.start) || today,
      end: toDateInput(absence?.end) || today,
      note: absence?.note || "",
      status: absence?.status || "pending",
    });
  }, [open, absence, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        start: form.start,
        end: form.end,
        note: form.note,
        ...(leader ? { status: form.status } : {}),
      };
      if (absence?.id) await api.put(`/absences/${absence.id}`, payload);
      else await api.post("/absences", { ...payload, user_id: form.user_id });
      toast.success(absence?.id ? t("absPage.saved") : t("absPage.created"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("absPage.deleteConfirm"))) return;
    try {
      await api.delete(`/absences/${absence.id}`);
      toast.success(t("absPage.deleted"));
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="absence-dialog" className="bg-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {absence?.id ? t("absPage.editTitle") : t("absPage.newTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {leader && !absence?.id && (
            <div>
              <L>{t("common.person")}</L>
              <Select value={form.user_id} onValueChange={(v) => set("user_id", v)}>
                <SelectTrigger data-testid="absence-user-select" className="mt-1 h-10">
                  <SelectValue placeholder={t("absPage.choosePerson")} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {staff?.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`absence-user-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>{t("common.type")}</L>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="absence-type-select" className="mt-1 h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {["vacation", "sick", "vab", "leave", "other"].map((k) => (
                    <SelectItem key={k} value={k} data-testid={`absence-type-option-${k}`}>
                      {t(`absType.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {leader && (
              <div>
                <L>{t("common.status")}</L>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="absence-status-select" className="mt-1 h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {["pending", "approved", "rejected"].map((k) => (
                      <SelectItem key={k} value={k} data-testid={`absence-status-option-${k}`}>
                        {t(`absStatus.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>{t("common.from")}</L>
              <Input
                type="date"
                data-testid="absence-start-input"
                value={form.start || ""}
                onChange={(e) => set("start", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
            <div>
              <L>{t("common.to")}</L>
              <Input
                type="date"
                data-testid="absence-end-input"
                value={form.end || ""}
                onChange={(e) => set("end", e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>
          <div>
            <L>Notering</L>
            <Textarea
              data-testid="absence-note-input"
              value={form.note || ""}
              onChange={(e) => set("note", e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {absence?.id ? (
            <Button
              variant="outline"
              data-testid="absence-delete-button"
              onClick={remove}
              className="border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t("common.delete")}
            </Button>
          ) : (
            <span />
          )}
          <Button
            data-testid="absence-save-button"
            onClick={save}
            disabled={busy}
            className="bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const NewButton = ({ onClick, label, testid }) => {
  const { company } = useAuth();
  if (company?.demo_readonly) return null;
  return (
    <Button
      data-testid={testid}
      onClick={onClick}
      className="h-10 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
    >
      <Plus className="mr-1.5 h-4 w-4" /> {label}
    </Button>
  );
};
